import * as React from "react";
import DateRange, {RangeToTimes} from "../../components/DateRange";
import Loading from "../../components/loading";
import {gql} from "@apollo/client";
import {mutate, query} from "../../lib/apollo";
import {
    Alert,
    Checkbox,
    FormControl,
    FormControlLabel,
    IconButton,
    Link as MuiLink,
    Menu,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TextField,
    Tooltip,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import moment from "moment";
import TableHeaderRow from "../../components/TableHeaderRow";
import MultiSelect from "../../components/multiSelect/multiSelect";
import {searchPubs} from "../blockedPublishers/blockedPublishersService";
import downloadCsv from "../../lib/downloadCsv";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {Link, useSearchParams} from "react-router-dom";
import {toast} from "react-toastify";

function stripColorHashes(text: string): string {
    return text.replace(/#/g, "");
}

function ColorSwatch({hex}: {hex?: string | null}) {
    if (!hex) {
        return null;
    }
    // Native title avoids 3x MUI Tooltip portals per row (~3000 for 1000 results)
    return (
        <Box
            title={hex}
            sx={{
                width: 20,
                height: 20,
                margin: "0 auto",
                border: "1px solid #444",
                backgroundColor: `#${hex}`,
            }}
        />
    );
}

type FingerprintRowData = {
    publisherLeadHitId: number;
    date: string;
    publisherId: number;
    publisherName?: string | null;
    ip?: string | null;
    c1?: string | null;
    c2?: string | null;
    c3?: string | null;
    loc?: string | null;
    ua?: string | null;
    httpAccept?: string | null;
    acceptLanguage?: string | null;
    acceptEncoding?: string | null;
    screen?: string | null;
    plugins?: string | null;
    keyword?: string | null;
    creativeId?: string | null;
    oa?: boolean | null;
    email?: string | null;
    cf?: string | null;
};

const FingerprintRow = React.memo(function FingerprintRow({
    row,
    open,
    stripeOdd,
    onToggleExpand,
    onOpenMenu,
}: {
    row: FingerprintRowData;
    open: boolean;
    stripeOdd: boolean;
    onToggleExpand: (id: number) => void;
    onOpenMenu: (el: HTMLElement, row: FingerprintRowData) => void;
}) {
    const bg = row.oa
        ? "rgba(211, 47, 47, 0.12)"
        : stripeOdd
            ? "white"
            : "#f9f9f9";

    return (
        <>
            <tr style={{backgroundColor: bg}}>
                <TableCell padding="checkbox">
                    <IconButton size="small" onClick={() => onToggleExpand(row.publisherLeadHitId)}>
                        {open ? <KeyboardArrowDownIcon/> : <KeyboardArrowRightIcon/>}
                    </IconButton>
                </TableCell>
                <TableCell>{row.date}</TableCell>
                <TableCell>
                    <MuiLink component={Link} to={`/publishers/${row.publisherId}`}>
                        {row.publisherName || "?"} ({row.publisherId})
                    </MuiLink>
                </TableCell>
                <TableCell>
                    {row.ip ? (
                        <MuiLink
                            href={`/fingerprint?ip=${encodeURIComponent(row.ip)}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {row.ip}
                        </MuiLink>
                    ) : null}
                </TableCell>
                <TableCell><ColorSwatch hex={row.c1}/></TableCell>
                <TableCell><ColorSwatch hex={row.c2}/></TableCell>
                <TableCell><ColorSwatch hex={row.c3}/></TableCell>
                <TableCell>{row.loc}</TableCell>
                <TableCell sx={{width: 630, minWidth: 630, wordBreak: "break-word"}}>{row.ua}</TableCell>
                <TableCell>{row.screen}</TableCell>
                <TableCell>
                    <IconButton
                        size="small"
                        onClick={(e) => onOpenMenu(e.currentTarget, row)}
                    >
                        <MoreVertIcon fontSize="small"/>
                    </IconButton>
                </TableCell>
            </tr>
            {open ? (
                <tr style={{backgroundColor: bg}}>
                    <TableCell colSpan={11} sx={{py: 0, borderTop: 0}}>
                        <Box sx={{p: 1.5, bgcolor: "action.hover", fontSize: 13}}>
                            <div><b>Keyword:</b> {row.keyword || "N/A"}</div>
                            <div><b>Plugins:</b> {row.plugins || "—"}</div>
                            <div><b>Creative ID:</b> {row.creativeId || "—"}</div>
                            {row.email ? (
                                <div>
                                    <b>Email:</b>{" "}
                                    <MuiLink
                                        href={`/lead?email=${encodeURIComponent(row.email)}`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {row.email}
                                    </MuiLink>
                                </div>
                            ) : null}
                            {row.cf ? <div><b>CF:</b> {row.cf}</div> : null}
                            <div><b>Accept:</b> {row.httpAccept || "—"}</div>
                            <div><b>Language:</b> {row.acceptLanguage || "—"}</div>
                            <div><b>Encoding:</b> {row.acceptEncoding || "—"}</div>
                        </Box>
                    </TableCell>
                </tr>
            ) : null}
        </>
    );
});

export default function FingerprintPage() {
    React.useEffect(() => {
        document.title = "Fingerprint | DN";
    }, []);

    const [searchParams] = useSearchParams();
    const [dateRange, setDateRange] = React.useState([] as any);
    const [filtersLoaded, setFiltersLoaded] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [results, setResults] = React.useState([] as FingerprintRowData[]);
    const [selectedPublishers, setSelectedPublishers] = React.useState([] as any[]);
    const [ip, setIp] = React.useState("");
    const [loc, setLoc] = React.useState("");
    const [ua, setUa] = React.useState("");
    const [c1, setC1] = React.useState("");
    const [c2, setC2] = React.useState("");
    const [c3, setC3] = React.useState("");
    const [oaOnly, setOaOnly] = React.useState(false);
    const [expandedIds, setExpandedIds] = React.useState<ReadonlySet<number>>(() => new Set());
    const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [menuRow, setMenuRow] = React.useState<FingerprintRowData | null>(null);

    React.useEffect(() => {
        const d = RangeToTimes("Last 7 Days");
        const ipFromQs = (searchParams.get("ip") || "").trim();
        setDateRange(d);
        if (ipFromQs) {
            setIp(ipFromQs);
        }
        setFiltersLoaded(true);
        reload(d, [], ipFromQs, "", "", "", "", "", false).then(() => {});
    }, []);

    const reload = async (
        date = dateRange,
        pubs = selectedPublishers,
        ipVal = ip,
        locVal = loc,
        uaVal = ua,
        c1Val = c1,
        c2Val = c2,
        c3Val = c3,
        oaVal = oaOnly,
    ) => {
        if (!date || date.length < 2) {
            toast.error("Select a date range");
            return;
        }
        setLoading(true);
        const q = gql`
            query q($input: FingerprintColorsInput!) {
                fingerprintColors(input: $input) {
                    publisherLeadHitId
                    date
                    publisherId
                    publisherName
                    ip
                    c1
                    c2
                    c3
                    loc
                    ua
                    httpAccept
                    acceptLanguage
                    acceptEncoding
                    screen
                    plugins
                    keyword
                    creativeId
                    oa
                    email
                    cf
                }
            }
        `;
        const variables = {
            input: {
                startDate: moment(date[0]).format("YYYY-MM-DD HH:mm:ss"),
                endDate: moment(date[1]).format("YYYY-MM-DD HH:mm:ss"),
                publisherIds: pubs.map((x: any) => x.id),
                ip: ipVal || null,
                loc: locVal || null,
                ua: uaVal || null,
                c1: stripColorHashes(c1Val).trim() || null,
                c2: stripColorHashes(c2Val).trim() || null,
                c3: stripColorHashes(c3Val).trim() || null,
                oaOnly: oaVal || null,
            },
        };
        const response = await query(q, variables);
        setResults(response?.fingerprintColors || []);
        setExpandedIds(new Set());
        setLoading(false);
    };

    const toggleExpand = React.useCallback((id: number) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const allExpanded = results.length > 0 && expandedIds.size === results.length;

    const toggleExpandAll = () => {
        React.startTransition(() => {
            if (allExpanded) {
                setExpandedIds(new Set());
                return;
            }
            setExpandedIds(new Set(results.map((row) => row.publisherLeadHitId)));
        });
    };

    const openMenu = React.useCallback((el: HTMLElement, row: FingerprintRowData) => {
        setMenuAnchor(el);
        setMenuRow(row);
    }, []);

    const toggleOa = async (row: FingerprintRowData) => {
        const next = !row.oa;
        const m = gql`
            mutation m($publisherLeadHitId: Int!, $oa: Boolean!) {
                fingerprintSetOa(publisherLeadHitId: $publisherLeadHitId, oa: $oa)
            }
        `;
        const ok = await mutate(m, {publisherLeadHitId: row.publisherLeadHitId, oa: next});
        if (ok?.fingerprintSetOa) {
            setResults((prev) => prev.map((r) => (
                r.publisherLeadHitId === row.publisherLeadHitId ? {...r, oa: next} : r
            )));
            toast.success(next ? "Marked OA" : "Cleared OA");
        } else {
            toast.error("Failed to toggle OA");
        }
        setMenuAnchor(null);
        setMenuRow(null);
    };

    const download = async () => {
        const content: any[][] = [[
            "Date", "Publisher", "PublisherId", "Ip", "Email", "CF",
            "C1", "C2", "C3", "Location", "User-Agent", "Accept", "Language", "Encoding",
            "Screen", "Plugins", "Keyword", "CreativeId", "OA",
        ]];
        results.forEach((r) => {
            content.push([
                r.date,
                r.publisherName,
                r.publisherId,
                r.ip,
                r.email || "",
                r.cf || "",
                r.c1 || "",
                r.c2 || "",
                r.c3 || "",
                r.loc || "",
                r.ua || "",
                r.httpAccept || "",
                r.acceptLanguage || "",
                r.acceptEncoding || "",
                r.screen || "",
                r.plugins || "",
                r.keyword || "",
                r.creativeId || "",
                r.oa ? "1" : "0",
            ]);
        });
        await downloadCsv("fingerprint.csv", content);
    };

    if (!filtersLoaded) {
        return <Loading/>;
    }

    return (
        <>
            <h2>Fingerprint</h2>
            <Box sx={{display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "flex-end", gap: 1}}>
                <DateRange showTime={false} dateRange={dateRange} setDateRange={setDateRange} style={{marginRight: "10px"}}/>
                <FormControl variant="standard" sx={{width: "200px"}}>
                    <MultiSelect
                        searchFunction={searchPubs}
                        selectedItems={selectedPublishers}
                        setSelectedItems={setSelectedPublishers}
                        placeholder={"Publishers"}
                    />
                </FormControl>
                <TextField variant="standard" label="IP" value={ip} onChange={(e) => setIp(e.target.value)} sx={{width: "100px"}}/>
                <TextField variant="standard" label="Loc" value={loc} onChange={(e) => setLoc(e.target.value)} sx={{width: "120px"}}/>
                <TextField variant="standard" label="UA" value={ua} onChange={(e) => setUa(e.target.value)} sx={{width: "140px"}}/>
                <FormControlLabel
                    control={<Checkbox checked={oaOnly} onChange={(e) => setOaOnly(e.target.checked)} size="small"/>}
                    label="OA"
                />
                <Button
                    onClick={() => reload()}
                    variant="contained"
                    color="success"
                    disabled={loading}
                >
                    Run
                </Button>
                {results.length > 0 && (
                    <IconButton aria-label="download" onClick={download} title="Download CSV">
                        <FileDownloadIcon/>
                    </IconButton>
                )}
            </Box>
            <Box sx={{display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 1, mt: 1.5, alignItems: "flex-start"}}>
                <TextField
                    label="C1"
                    value={c1}
                    onChange={(e) => setC1(stripColorHashes(e.target.value))}
                    size="small"
                    sx={{width: "140px"}}
                    placeholder="hex color"
                />
                <TextField
                    label="C2"
                    value={c2}
                    onChange={(e) => setC2(stripColorHashes(e.target.value))}
                    size="small"
                    sx={{width: "140px"}}
                    placeholder="hex color"
                />
                <TextField
                    label="C3"
                    value={c3}
                    onChange={(e) => setC3(stripColorHashes(e.target.value))}
                    size="small"
                    sx={{width: "140px"}}
                    placeholder="hex color"
                />
            </Box>

            <p></p>
            {loading ? <Loading/> :
                results.length === 0 ? <Alert severity="warning">No results found.</Alert> :
                    <TableContainer component={Paper} sx={{maxWidth: "calc(100vw - 10px)"}}>
                        <Table
                            size="small"
                            sx={{"& .MuiTableCell-root": {px: 0.5}}}
                        >
                            <TableHead>
                                <TableHeaderRow>
                                    <TableCell padding="checkbox">
                                        <Tooltip title={allExpanded ? "Collapse all" : "Expand all"}>
                                            <IconButton size="small" onClick={toggleExpandAll}>
                                                {allExpanded ? <KeyboardArrowDownIcon/> : <KeyboardArrowRightIcon/>}
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Pub</TableCell>
                                    <TableCell>Ip</TableCell>
                                    <TableCell>C1</TableCell>
                                    <TableCell>C2</TableCell>
                                    <TableCell>C3</TableCell>
                                    <TableCell>Location</TableCell>
                                    <TableCell>User-Agent</TableCell>
                                    <TableCell>Screen</TableCell>
                                    <TableCell/>
                                </TableHeaderRow>
                            </TableHead>
                            <TableBody>
                                {results.map((row, index) => (
                                    <FingerprintRow
                                        key={row.publisherLeadHitId}
                                        row={row}
                                        open={expandedIds.has(row.publisherLeadHitId)}
                                        stripeOdd={index % 2 === 0}
                                        onToggleExpand={toggleExpand}
                                        onOpenMenu={openMenu}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
            }

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => {
                    setMenuAnchor(null);
                    setMenuRow(null);
                }}
            >
                <MenuItem
                    onClick={() => menuRow && toggleOa(menuRow)}
                    disabled={!menuRow}
                >
                    Toggle OA
                </MenuItem>
            </Menu>
        </>
    );
}
